import { useEffect, useState } from "react";
import { collection, query, where, getDocs, limit, orderBy } from "firebase/firestore";
import { db } from "../../firebase.config";
import { earlyBirdPlan, normalPlan, initialPlan } from "../../constants/plans";

const SubscriptionModal = ({ setShowSubscription, showSubscription }) => {
  const [paymentDate, setPaymenDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [planName, setPlanName] = useState(null);
  const [plan, setSelectedPlan] = useState(null);
  
  useEffect(() => {
    const planMap = {
      [earlyBirdPlan?.name]: earlyBirdPlan,
      [normalPlan?.name]: normalPlan,
      [initialPlan?.name]: initialPlan,
    };
    
    const selectedPlan = planMap[planName] || null;
    setSelectedPlan(selectedPlan);
  }, [planName]);

  const handleCloseModal = () => {
    setShowSubscription(false);
  };

  useEffect(() => {
    const fetchPaymentDate = async () => {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const uid = authUser?.uid;

      if (!uid) {
        console.warn("No user found in localStorage.");
        return;
      }

      try {
        const singleQuery = query(
          collection(db, "orders"),
          where("userId", "==", uid),
          orderBy("createdAt", "desc"),
          limit(1)
        );

        const querySnapshot = await getDocs(singleQuery);

        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const record = { id: doc.id, ...doc.data() };

          const createdAt = record.createdAt?.toDate();

          if (createdAt) {
            const formattedDate = createdAt.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            });

            setPaymenDate(formattedDate);
            setPlanName(record?.name);
          }
        } else {
          setPaymenDate("");
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching record:", error);
      }
    };

    fetchPaymentDate();
  }, []);

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="bg-white p-6 rounded-xl shadow-xl text-center flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-700">Loading subscribed plan details...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {showSubscription && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4">
          <div
            className="bg-white text-gray-900 border border-gray-200 rounded-xl shadow-xl w-full max-w-[95%] sm:max-w-md md:max-w-lg lg:max-w-xl relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative px-4 sm:px-6 py-4 border-b border-gray-300 text-center">
              <h2 className="text-xl sm:text-2xl font-bold">Subscribed Plan</h2>
              <button
                onClick={handleCloseModal}
                className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 text-gray-500 hover:text-black text-2xl font-bold"
              >
                &times;
              </button>
            </div>

            {/* Content */}
            <div className="px-4 sm:px-6 py-6">
              {/* Plan Name */}
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold mb-2">
                {plan?.name}
              </h3>

              {/* Price */}
              <p className="text-base sm:text-lg md:text-xl font-bold mb-2">
                ${plan?.price}
                {plan?.isMonthly ? "/month" : ""}
              </p>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-6">{plan?.desc}</p>

              {/* Paid at info */}
              <div className="w-full border-2 py-2 mb-4 rounded-lg bg-green-100 text-green-800 text-center font-semibold text-sm sm:text-base border-green-300">
                Paid at {paymentDate}
              </div>

              <hr className="my-4 border-gray-300" />

              {/* Features */}
              <ul className="list-disc list-inside space-y-2">
                {plan?.features.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-gray-700 text-sm sm:text-base"
                  >
                    <svg
                      className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SubscriptionModal;
